/**
 * Realtor Settings — Syndication API Keys, then the WhatsApp Business
 * connection (`WhatsappConnectionCard`), then the daily follow-up email
 * (`DigestSettingsCard`, last on the page).
 *
 * Lets a verified realtor manage the API keys that authenticate their external
 * website against the public listings feed. Generation reveals the full secret
 * exactly once (show-once modal); the list only ever holds masked keys.
 */

import {
	Check,
	Copy,
	KeyRound,
	Loader2,
	RefreshCw,
	Trash2,
	TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { DigestSettingsCard } from "@/features/realtor/components/settings/digest-settings";
import { WhatsappConnectionCard } from "@/features/realtor/components/settings/whatsapp-connection-card";
import {
	type CreatedApiKey,
	createApiKey,
	listApiKeys,
	type MaskedApiKey,
	revokeApiKey,
	updateApiKeyDomains,
} from "@/features/realtor/services/api-keys-api.service";
import { copyToClipboard } from "@/features/realtor/utils/share-listing";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; keys: MaskedApiKey[] };

function formatDate(value: string | null, fallback: string): string {
	return value ? new Date(value).toLocaleDateString() : fallback;
}

function CopyButton({
	value,
	label,
	onCopied,
}: {
	value: string;
	label: string;
	onCopied?: () => void;
}) {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);
	return (
		<Button
			type="button"
			variant="outline"
			size="sm"
			className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
			onClick={async () => {
				const ok = await copyToClipboard(value);
				if (!ok) {
					toast.error(t("realtor.settings.copy.failed"));
					return;
				}
				setCopied(true);
				onCopied?.();
				toast.success(t("realtor.settings.copy.copiedToast"));
				setTimeout(() => setCopied(false), 1500);
			}}
		>
			{copied ? (
				<Check
					className="h-4 w-4"
					style={{ color: "var(--color-dashboard-pill-done)" }}
				/>
			) : (
				<Copy className="h-4 w-4" />
			)}
			{copied ? t("realtor.settings.copy.copied") : label}
		</Button>
	);
}

export function RealtorSettingsPage() {
	const { t } = useTranslation();
	const [state, setState] = useState<LoadState>({ status: "loading" });
	const [generateOpen, setGenerateOpen] = useState(false);
	const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);
	const [revokeTarget, setRevokeTarget] = useState<MaskedApiKey | null>(null);
	const [domainsTarget, setDomainsTarget] = useState<MaskedApiKey | null>(null);

	const load = useCallback(async () => {
		setState({ status: "loading" });
		const result = await listApiKeys();
		if (result.success && result.data) {
			setState({ status: "loaded", keys: result.data });
		} else {
			setState({
				status: "error",
				message: result.error ?? t("realtor.settings.loadFailedFallback"),
			});
		}
	}, [t]);

	useEffect(() => {
		void load();
	}, [load]);

	const activeKeys =
		state.status === "loaded" ? state.keys.filter((k) => !k.revokedAt) : [];
	const neverUsed = t("realtor.settings.neverUsed");

	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-6">
			<div>
				<p
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 11,
						fontWeight: 600,
						letterSpacing: "0.18em",
						textTransform: "uppercase",
						color: "var(--color-dashboard-muted)",
					}}
				>
					Realtor Portal
				</p>
				<h1
					style={{
						fontFamily: "var(--font-display)",
						fontSize: 32,
						fontWeight: 500,
						fontStyle: "italic",
						color: "var(--color-dashboard-text)",
						marginTop: 8,
						lineHeight: 1.1,
					}}
				>
					{t("realtor.settings.title")}
				</h1>
				<p
					className="mt-2 text-sm"
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					{t("realtor.settings.subtitle")}
				</p>
			</div>

			{state.status === "loading" && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="space-y-3 p-6">
						<Skeleton className="h-12 w-full" />
						<Skeleton className="h-12 w-full" />
					</CardContent>
				</Card>
			)}

			{state.status === "error" && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6" role="alert">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-pill-risk)" }}
						>
							{state.message}
						</p>
						<StudioButton
							variant="secondary"
							className="mt-4 gap-1.5"
							onClick={() => void load()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.settings.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			)}

			{state.status === "loaded" && state.keys.length === 0 && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-8 text-center space-y-3">
						<KeyRound
							className="h-8 w-8 mx-auto"
							style={{ color: "var(--color-dashboard-muted)" }}
						/>
						<div>
							<p
								className="text-sm font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.settings.empty.title")}
							</p>
							<p
								className="text-xs mt-1"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.settings.empty.description")}
							</p>
						</div>
						<StudioButton
							variant="primary"
							size="sm"
							className="gap-1.5"
							onClick={() => setGenerateOpen(true)}
						>
							<KeyRound className="h-4 w-4" />
							{t("realtor.settings.empty.action")}
						</StudioButton>
					</CardContent>
				</Card>
			)}

			{state.status === "loaded" && state.keys.length > 0 && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-4">
						<div className="mb-3 flex items-center justify-between">
							<h2
								className="text-sm font-medium"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.settings.list.heading")}
							</h2>
							<StudioButton
								variant="primary"
								size="sm"
								className="gap-1.5"
								onClick={() => setGenerateOpen(true)}
							>
								<KeyRound className="h-4 w-4" />
								{t("realtor.settings.list.generate")}
							</StudioButton>
						</div>
						<ul className="space-y-2">
							{state.keys.map((key) => (
								<li
									key={key.id}
									className="rounded-md border p-3"
									style={{
										backgroundColor: "var(--color-dashboard-surface)",
										borderColor: "var(--color-dashboard-border)",
									}}
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p
												className="truncate text-sm font-medium"
												style={{ color: "var(--color-dashboard-text)" }}
											>
												{key.name}
											</p>
											<p
												className="font-mono text-[11px]"
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{key.keyPrefix}
												{"••••"}
											</p>
											<p
												className="mt-1 text-[11px]"
												style={{ color: "var(--color-dashboard-muted)" }}
											>
												{t("realtor.settings.createdLabel", {
													date: formatDate(key.createdAt, neverUsed),
												})}{" "}
												·{" "}
												{t("realtor.settings.lastUsedLabel", {
													date: formatDate(key.lastUsedAt, neverUsed),
												})}
											</p>
											{key.allowedDomains.length > 0 && (
												<p
													className="mt-1 text-[11px]"
													style={{ color: "var(--color-dashboard-muted)" }}
												>
													{t("realtor.settings.domainsPrefix", {
														domains: key.allowedDomains.join(", "),
													})}
												</p>
											)}
										</div>
										<div className="flex shrink-0 flex-col items-end gap-1.5">
											{key.revokedAt ? (
												<span
													className="rounded-full px-2 py-0.5 text-[11px]"
													style={{
														backgroundColor: "var(--color-dashboard-surface)",
														color: "var(--color-dashboard-muted)",
													}}
												>
													{t("realtor.settings.revokedBadge")}
												</span>
											) : (
												<>
													<Button
														variant="outline"
														size="sm"
														className="h-7 text-[11px] border-dashboard-border bg-dashboard-bg"
														onClick={() => setDomainsTarget(key)}
													>
														{t("realtor.settings.list.domains")}
													</Button>
													<Button
														variant="outline"
														size="sm"
														className="h-7 gap-1 text-[11px] border-dashboard-border bg-dashboard-bg"
														style={{
															color: "var(--color-dashboard-pill-risk)",
														}}
														onClick={() => setRevokeTarget(key)}
													>
														<Trash2 className="h-3 w-3" />
														{t("realtor.settings.list.revoke")}
													</Button>
												</>
											)}
										</div>
									</div>
								</li>
							))}
						</ul>
					</CardContent>
				</Card>
			)}

			<IntegrationDocs />

			<WhatsappConnectionCard />

			<DigestSettingsCard />

			<GenerateDialog
				open={generateOpen}
				onOpenChange={setGenerateOpen}
				onCreated={(created) => {
					setGenerateOpen(false);
					setRevealed(created);
					void load();
				}}
			/>

			<RevealDialog created={revealed} onClose={() => setRevealed(null)} />

			<RevokeDialog
				target={revokeTarget}
				isLastActive={activeKeys.length === 1}
				onClose={() => setRevokeTarget(null)}
				onRevoked={() => {
					setRevokeTarget(null);
					void load();
				}}
			/>

			<DomainsDialog
				target={domainsTarget}
				onClose={() => setDomainsTarget(null)}
				onSaved={() => {
					setDomainsTarget(null);
					void load();
				}}
			/>
		</div>
	);
}

function GenerateDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: (created: CreatedApiKey) => void;
}) {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setName("");
			setError(null);
		}
	}, [open]);

	const submit = async () => {
		if (!name.trim()) {
			setError(t("realtor.settings.generate.nameRequired"));
			return;
		}
		setBusy(true);
		const result = await createApiKey(name.trim());
		setBusy(false);
		if (result.success && result.data) {
			onCreated(result.data);
		} else {
			setError(
				result.error ?? t("realtor.settings.generate.createFailedFallback"),
			);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
				<DialogHeader>
					<DialogTitle
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 20,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
						}}
					>
						{t("realtor.settings.generate.title")}
					</DialogTitle>
					<DialogDescription className="text-sm text-dashboard-muted">
						{t("realtor.settings.generate.description")}
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-2">
					<Label htmlFor="key-name" className="text-sm text-dashboard-text">
						{t("realtor.settings.generate.nameLabel")}
					</Label>
					<Input
						id="key-name"
						value={name}
						placeholder={t("realtor.settings.generate.namePlaceholder")}
						aria-invalid={error ? true : undefined}
						onChange={(e) => {
							setName(e.target.value);
							if (error) setError(null);
						}}
						className="border-dashboard-border bg-dashboard-surface text-dashboard-text"
					/>
					{error && <p className="text-xs text-dashboard-pill-risk">{error}</p>}
				</div>
				<DialogFooter>
					<StudioButton
						variant="secondary"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						{t("realtor.settings.generate.cancel")}
					</StudioButton>
					<StudioButton onClick={() => void submit()} disabled={busy}>
						{busy && <Loader2 className="h-4 w-4 animate-spin" />}
						{t("realtor.settings.generate.submit")}
					</StudioButton>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function RevealDialog({
	created,
	onClose,
}: {
	created: CreatedApiKey | null;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const [copiedOnce, setCopiedOnce] = useState(false);
	const [confirmClose, setConfirmClose] = useState(false);

	useEffect(() => {
		if (created) setCopiedOnce(false);
	}, [created]);

	if (!created) return null;

	const requestClose = () => {
		if (!copiedOnce) {
			setConfirmClose(true);
			return;
		}
		onClose();
	};

	return (
		<>
			<Dialog
				open={!confirmClose}
				onOpenChange={(next) => {
					if (!next) requestClose();
				}}
			>
				<DialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<DialogHeader>
						<DialogTitle
							style={{
								fontFamily: "var(--font-display)",
								fontSize: 20,
								fontWeight: 500,
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("realtor.settings.reveal.title")}
						</DialogTitle>
						<DialogDescription className="text-sm text-dashboard-muted">
							{t("realtor.settings.reveal.description")}
						</DialogDescription>
					</DialogHeader>

					<div
						className="flex items-start gap-2 rounded-md border p-3"
						style={{
							borderColor: "var(--color-dashboard-pill-awaiting)",
							backgroundColor: "var(--color-dashboard-pill-awaiting-bg)",
						}}
					>
						<TriangleAlert
							className="mt-0.5 h-4 w-4 shrink-0"
							style={{ color: "var(--color-dashboard-pill-awaiting)" }}
						/>
						<p
							className="text-xs"
							style={{ color: "var(--color-dashboard-text)" }}
						>
							{t("realtor.settings.reveal.warning")}
						</p>
					</div>

					<div
						className="break-all rounded-md px-3 py-2 font-mono text-xs"
						style={{
							backgroundColor: "var(--color-dashboard-surface)",
							color: "var(--color-dashboard-text)",
						}}
					>
						{created.key}
					</div>

					<DialogFooter>
						<CopyButton
							value={created.key}
							label={t("realtor.settings.reveal.copy")}
							onCopied={() => setCopiedOnce(true)}
						/>
						<StudioButton variant="primary" onClick={requestClose}>
							{t("realtor.settings.reveal.done")}
						</StudioButton>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
				<AlertDialogContent className="bg-dashboard-bg border-dashboard-border rounded-xl">
					<AlertDialogHeader>
						<AlertDialogTitle className="text-dashboard-text">
							{t("realtor.settings.reveal.confirmTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-dashboard-muted">
							{t("realtor.settings.reveal.confirmBody")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="border-dashboard-border text-dashboard-text">
							{t("realtor.settings.reveal.keepOpen")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setConfirmClose(false);
								onClose();
							}}
						>
							{t("realtor.settings.reveal.closeAnyway")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function RevokeDialog({
	target,
	isLastActive,
	onClose,
	onRevoked,
}: {
	target: MaskedApiKey | null;
	isLastActive: boolean;
	onClose: () => void;
	onRevoked: () => void;
}) {
	const { t } = useTranslation();
	const [busy, setBusy] = useState(false);
	if (!target) return null;

	const revoke = async () => {
		setBusy(true);
		const result = await revokeApiKey(target.id);
		setBusy(false);
		if (result.success) {
			toast.success(t("realtor.settings.revoke.revokedToast"));
			onRevoked();
		} else {
			toast.error(result.error ?? t("realtor.settings.revoke.failedFallback"));
		}
	};

	return (
		<AlertDialog open onOpenChange={(next) => !next && onClose()}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						{t("realtor.settings.revoke.title")}
					</AlertDialogTitle>
					<AlertDialogDescription>
						{t("realtor.settings.revoke.bodyBefore")}{" "}
						<strong>{target.name}</strong> (
						<span className="font-mono">{target.keyPrefix}••••</span>){" "}
						{t("realtor.settings.revoke.bodyAfter")}
						{isLastActive && (
							<span
								className="mt-2 block"
								style={{ color: "var(--color-dashboard-pill-awaiting)" }}
							>
								{t("realtor.settings.revoke.lastActiveWarning")}
							</span>
						)}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={busy} onClick={onClose}>
						{t("realtor.settings.revoke.keep")}
					</AlertDialogCancel>
					<AlertDialogAction
						disabled={busy}
						className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						onClick={(e) => {
							e.preventDefault();
							void revoke();
						}}
					>
						{t("realtor.settings.revoke.confirm")}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

function DomainsDialog({
	target,
	onClose,
	onSaved,
}: {
	target: MaskedApiKey | null;
	onClose: () => void;
	onSaved: () => void;
}) {
	const { t } = useTranslation();
	const [domains, setDomains] = useState<string[]>([]);
	const [draft, setDraft] = useState("");
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (target) {
			setDomains(target.allowedDomains);
			setDraft("");
		}
	}, [target]);

	if (!target) return null;

	const addDraft = () => {
		const host = draft
			.trim()
			.toLowerCase()
			.replace(/^https?:\/\//, "")
			.replace(/^www\./, "")
			.replace(/\/.*$/, "");
		if (!host) return;
		if (!domains.includes(host)) setDomains([...domains, host]);
		setDraft("");
	};

	const save = async () => {
		setBusy(true);
		const result = await updateApiKeyDomains(target.id, domains);
		setBusy(false);
		if (result.success) {
			toast.success(t("realtor.settings.domainsDialog.updatedToast"));
			onSaved();
		} else {
			toast.error(
				result.error ?? t("realtor.settings.domainsDialog.failedFallback"),
			);
		}
	};

	return (
		<Dialog open onOpenChange={(next) => !next && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("realtor.settings.domainsDialog.title")}</DialogTitle>
					<DialogDescription>
						{t("realtor.settings.domainsDialog.description")}
					</DialogDescription>
				</DialogHeader>

				<div className="flex gap-2">
					<Input
						value={draft}
						placeholder={t("realtor.settings.domainsDialog.placeholder")}
						onChange={(e) => setDraft(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								addDraft();
							}
						}}
					/>
					<Button type="button" variant="outline" onClick={addDraft}>
						{t("realtor.settings.domainsDialog.add")}
					</Button>
				</div>

				{domains.length > 0 ? (
					<div className="flex flex-wrap gap-2">
						{domains.map((d) => (
							<span
								key={d}
								className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
								style={{
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-text)",
								}}
							>
								{d}
								<button
									type="button"
									aria-label={t("realtor.settings.domainsDialog.removeAria", {
										domain: d,
									})}
									className="hover:opacity-70"
									style={{ color: "var(--color-dashboard-muted)" }}
									onClick={() => setDomains(domains.filter((x) => x !== d))}
								>
									×
								</button>
							</span>
						))}
					</div>
				) : (
					<p
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.settings.domainsDialog.noRestrictions")}
					</p>
				)}

				<DialogFooter>
					<Button variant="outline" disabled={busy} onClick={onClose}>
						{t("realtor.settings.domainsDialog.cancel")}
					</Button>
					<Button
						disabled={busy}
						className="gap-1.5"
						onClick={() => void save()}
					>
						{busy && <Loader2 className="h-4 w-4 animate-spin" />}
						{t("realtor.settings.domainsDialog.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function IntegrationDocs() {
	const { t } = useTranslation();
	const feedSample = `curl https://app.finishd.com/api/v1/feed/listings \\
  -H "X-Finishd-Key: YOUR_KEY"`;
	const inquirySample = `curl -X POST https://app.finishd.com/api/v1/feed/inquiry \\
  -H "X-Finishd-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"listingSlug":"...","name":"...","phone":"...","inquiryToken":"<from feed>"}'`;

	return (
		<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="space-y-4 p-4">
				<div>
					<h2
						className="text-sm font-medium"
						style={{ color: "var(--color-dashboard-text)" }}
					>
						{t("realtor.settings.docs.heading")}
					</h2>
					<p
						className="text-xs"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.settings.docs.introBefore")}{" "}
						<span className="font-mono">X-Finishd-Key</span>{" "}
						{t("realtor.settings.docs.introAfter")}
					</p>
				</div>

				<div className="space-y-1">
					<p
						className="text-xs font-medium"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.settings.docs.fetchListings")}
					</p>
					<pre
						className="overflow-x-auto rounded-md p-3 font-mono text-xs"
						style={{
							backgroundColor: "var(--color-dashboard-surface)",
							color: "var(--color-dashboard-text)",
						}}
					>
						{feedSample}
					</pre>
				</div>

				<div className="space-y-1">
					<p
						className="text-xs font-medium"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.settings.docs.submitInquiry")}
					</p>
					<pre
						className="overflow-x-auto rounded-md p-3 font-mono text-xs"
						style={{
							backgroundColor: "var(--color-dashboard-surface)",
							color: "var(--color-dashboard-text)",
						}}
					>
						{inquirySample}
					</pre>
				</div>

				<p
					className="text-[11px]"
					style={{ color: "var(--color-dashboard-muted)" }}
				>
					{t("realtor.settings.docs.footerBefore")}{" "}
					<span className="font-mono">&lt;img src&gt;</span>
					{t("realtor.settings.docs.footerMiddle")}{" "}
					<span className="font-mono">inquiryToken</span>{" "}
					{t("realtor.settings.docs.footerAfter")}
				</p>
			</CardContent>
		</Card>
	);
}
